/**
 * Shared Report Utilities
 */

/** Convert ISO date (YYYY-MM-DD) to Intacct format (MM/DD/YYYY) */
export const toIntacctDate = (isoDate) => {
  if (!isoDate) return '';
  const parts = isoDate.split('-');
  if (parts.length !== 3) return isoDate;
  return `${parts[1]}/${parts[2]}/${parts[0]}`;
};

/** Check if an as-of date is approximately "today" (within 2 days) */
export const isDateCurrent = (isoDate) => {
  const asOf = new Date(isoDate + 'T23:59:59');
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return Math.abs(asOf - today) < (2 * 24 * 60 * 60 * 1000);
};

/** Parse Intacct date strings like "8/30/2024", "8/30/2024 14:50", or "2025-08-22 00:00:00" */
export const parseDate = (dateStr) => {
  if (!dateStr || dateStr === '') return null;
  const s = String(dateStr).trim().split(' ')[0];

  // Try MM/DD/YYYY
  if (s.includes('/')) {
    const parts = s.split('/');
    if (parts.length === 3) {
      const m = parseInt(parts[0], 10) - 1;
      const d = parseInt(parts[1], 10);
      const y = parseInt(parts[2], 10);
      if (!isNaN(m) && !isNaN(d) && !isNaN(y)) return new Date(y, m, d);
    }
  }

  // Try YYYY-MM-DD (ISO)
  if (s.includes('-')) {
    const parts = s.split('-');
    if (parts.length === 3) {
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      const d = parseInt(parts[2], 10);
      if (!isNaN(y) && !isNaN(m) && !isNaN(d)) return new Date(y, m, d);
    }
  }

  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};

/** Build aging bucket configuration from comma-separated boundaries string */
export const buildAgingBuckets = (bucketString) => {
  const bucketDays = (bucketString || '30,60,90')
    .split(',')
    .map(s => parseInt(s.trim(), 10))
    .filter(n => !isNaN(n) && n > 0)
    .sort((a, b) => a - b);

  const bucketColumns = ['Current'];
  for (let i = 0; i < bucketDays.length; i++) {
    const start = i === 0 ? 1 : bucketDays[i - 1] + 1;
    bucketColumns.push(`${start}-${bucketDays[i]} Days`);
  }
  bucketColumns.push(`${bucketDays[bucketDays.length - 1] || 0}+ Days`);

  const getBucketIndex = (daysPastDue) => {
    if (daysPastDue <= 0) return 0;
    for (let i = 0; i < bucketDays.length; i++) {
      const start = i === 0 ? 1 : bucketDays[i - 1] + 1;
      if (daysPastDue >= start && daysPastDue <= bucketDays[i]) return i + 1;
    }
    return bucketColumns.length - 1;
  };

  return { bucketDays, bucketColumns, getBucketIndex };
};

/** Build payment lookup: groups by RECORDKEY, sums AMOUNT (negatives cancel) */
export const buildPaymentLookup = (paymentRecords) => {
  const lookup = {};
  for (const pmt of paymentRecords) {
    const key = String(pmt.RECORDKEY || '').trim();
    if (!key) continue;
    lookup[key] = (lookup[key] || 0) + (parseFloat(pmt.AMOUNT) || 0);
  }
  return lookup;
};

/** Aggregate line items by parent key: Balance = AMOUNT - TOTALPAID - TRX_AMOUNTRETAINED */
export const aggregateLineBalances = (lineItems, parentKeyField) => {
  const grouped = {};
  for (const line of lineItems) {
    const parentKey = String(line[parentKeyField] || '').trim();
    if (!parentKey) continue;
    if (!grouped[parentKey]) grouped[parentKey] = { balanceDue: 0, retainageHeld: 0, lineCount: 0 };

    const amount = parseFloat(line.AMOUNT) || 0;
    const totalPaid = parseFloat(line.TOTALPAID) || 0;
    const retained = parseFloat(line.TRX_AMOUNTRETAINED) || 0;

    grouped[parentKey].balanceDue += (amount - totalPaid - retained);
    grouped[parentKey].retainageHeld += retained;
    grouped[parentKey].lineCount++;
  }
  return grouped;
};

/**
 * Build retainage release lookup by joining release headers to entries.
 * 
 * Intacct's system report shows retainage as released starting the day AFTER the release date.
 * So we use strict less-than: RELEASEDATE < asOfDate.
 * Only includes headers where STATE = 'Released'.
 * 
 * @param {Array} releaseHeaders - APRETAINAGERELEASE or ARRETAINAGERELEASE records
 * @param {Array} releaseEntries - APRETAINAGERELEASEENTRY or ARRETAINAGERELEASEENTRY records
 * @param {string} parentKeyField - 'RETAINAGEBILLKEY' or 'RETAINAGEINVOICEKEY' on entries
 * @param {Date} asOfDate - as-of date for the report
 * @returns {Object} { parentRecordNo: totalReleased }
 */
export const buildRetainageReleaseLookup = (releaseHeaders, releaseEntries, parentKeyField, asOfDate) => {
  // Step 1: Find valid release header RECORDNOs (Released state, RELEASEDATE strictly before as-of)
  const validReleaseIds = new Set();
  for (const header of releaseHeaders) {
    const state = String(header.STATE || '').trim();
    if (state !== 'Released') continue;

    const releaseDate = parseDate(header.RELEASEDATE || header.GLPOSTINGDATE);
    if (!releaseDate) continue;

    // Strict less-than: release on 2/14 only shows starting 2/15
    if (releaseDate < asOfDate) {
      validReleaseIds.add(String(header.RECORDNO || '').trim());
    }
  }

  // Step 2: Sum TRX_AMOUNTRELEASED from entries whose RRKEY is in valid set
  const lookup = {};
  for (const entry of releaseEntries) {
    const rrKey = String(entry.RRKEY || '').trim();
    if (!validReleaseIds.has(rrKey)) continue;

    const parentKey = String(entry[parentKeyField] || '').trim();
    if (!parentKey) continue;

    lookup[parentKey] = (lookup[parentKey] || 0) + (parseFloat(entry.TRX_AMOUNTRELEASED) || 0);
  }

  return lookup;
};